import { useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { GAME, LANES, PLAYER_Z } from "./constants";
import { DIFFICULTIES } from "./difficulty";
import { activeDifficulty, activeStage, equippedVehicle, useGame } from "./store";
import { MAGNET_RADIUS } from "./vehicles";
import { sfx } from "../lib/audio";
import { VehicleModel } from "./VehicleModel";
import { makeCoinMesh, makeJumpTokenMesh } from "./models";
import {
  disposeHazardObject,
  makeHazardObject,
  preloadHazardModels,
  centerOnAabb,
  plantOnGround,
} from "./hazardModels";
import { HAZARD_BOX, LANE_LERP, PICKUP_HALF_X, PICKUP_HALF_Z, PLAYER_HALF_X, PLAYER_HALF_Z, VISUAL_FIT, hitboxesEnabled, hitsPickup, jumpClears, playerHitsHazard, stackLane, type HazardKind } from "./collide";
import { FIRST_WAVE_AT, TRACK_LOOKAHEAD, makeWave, waveGap } from "./track";

type Hazard = {
  id: number;
  z: number;
  prevZ: number;
  lane: number;
  kind: HazardKind;
  halfX: number;
  halfZ: number;
};

type Pickup = {
  id: number;
  z: number;
  lane: number;
  kind: "coin" | "jump";
  /** World X when magnet is dragging (otherwise lane X). */
  x?: number;
};

let nextId = 1;

export function LaneWorld() {
  useEffect(() => {
    void preloadHazardModels();
  }, []);

  const { camera } = useThree();
  const player = useRef<THREE.Group>(null);
  const worldScroll = useRef(0);
  const dashGroup = useRef<THREE.Group>(null);
  const cityGroup = useRef<THREE.Group>(null);
  const rainRef = useRef<THREE.Points>(null);
  const hazardGroup = useRef<THREE.Group>(null);
  const trailPts = useRef<THREE.Points>(null);
  const nextWaveIndex = useRef(0);
  const nextWaveAt = useRef(FIRST_WAVE_AT);
  const hazards = useRef<Hazard[]>([]);
  const pickups = useRef<Pickup[]>([]);
  const lastClear = useRef(0);
  const lastPhase = useRef(useGame.getState().phase);
  const rainFrame = useRef(0);
  const playerMats = useRef<THREE.MeshStandardMaterial[]>([]);
  const playerMatsKey = useRef<string | null>(null);
  const playerHitbox = useRef<THREE.Mesh>(null);
  const ghostRef = useRef<THREE.Group>(null);
  const showHitbox = useRef(hitboxesEnabled());
  const playerHalf = useRef({ x: PLAYER_HALF_X, z: PLAYER_HALF_Z });
  const ghostSampleAcc = useRef(0);

  const trailBuf = useMemo(() => {
    const n = 48;
    const pos = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      pos[i * 3 + 2] = 4 + i * 0.12;
    }
    return { n, pos };
  }, []);

  const vehicle = equippedVehicle();
  const equippedId = useGame((s) => s.equippedVehicle);
  const equippedStageId = useGame((s) => s.equippedStage);
  const duelStage = useGame((s) => s.duel?.stage);
  const stage = activeStage();
  void equippedId;
  void equippedStageId;
  void duelStage;

  const rainGeo = useMemo(() => {
    const count = 400;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 18;
      pos[i * 3 + 1] = Math.random() * 14;
      pos[i * 3 + 2] = -Math.random() * 70;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  }, []);

  const dashes = useMemo(() => {
    const items: { x: number; z: number }[] = [];
    for (const x of [-0.72, 0.72]) {
      for (let z = -70; z < 20; z += 3.2) items.push({ x, z });
    }
    return items;
  }, []);

  const buildings = useMemo(() => {
    const list: { x: number; h: number; z: number; w: number; hue: string }[] =
      [];
    for (let i = 0; i < 28; i++) {
      const side = i % 2 === 0 ? -1 : 1;
      list.push({
        x: side * (5.2 + Math.random() * 3.5),
        h: 3 + Math.random() * 9,
        z: -65 + i * 4.6 + Math.random() * 1.5,
        w: 1.4 + Math.random() * 1.8,
        hue: Math.random() > 0.5 ? stage.buildingHueA : stage.buildingHueB,
      });
    }
    return list;
  }, [stage.buildingHueA, stage.buildingHueB, stage.id]);

  useEffect(() => {
    camera.position.set(0, 5.8, 11.2);
    camera.lookAt(0, 0.6, -6);
  }, [camera]);

  useFrame((state, dt) => {
    const g = useGame.getState();
    const clampedDt = Math.min(dt, 0.05);
    const v = equippedVehicle();
    const now = g.paused ? g.pauseHeldAt || performance.now() : performance.now();
    const powerOn = now < g.powerActiveUntil;
    const phaseOn = powerOn && v.power.id === "phase";
    const magnetOn = powerOn && v.power.id === "magnet";
    const crushCrates = v.power.id === "crush_crates";

    if (g.clearToken !== lastClear.current) {
      lastClear.current = g.clearToken;
      hazards.current = hazards.current.filter(
        (h) => h.z < PLAYER_Z - GAME.continueClearAhead || h.z > PLAYER_Z + 3,
      );
      pickups.current = pickups.current.filter(
        (p) => p.z < PLAYER_Z - GAME.continueClearAhead || p.z > PLAYER_Z + 3,
      );
    }

    if (g.phase === "playing" && lastPhase.current !== "playing") {
      hazards.current = [];
      pickups.current = [];
      nextWaveIndex.current = 0;
      nextWaveAt.current = FIRST_WAVE_AT;
      if (player.current) player.current.position.x = LANES[g.lane];
    }
    if (g.phase === "dead" && lastPhase.current === "playing") {
      sfx.crash();
    }
    lastPhase.current = g.phase;

    if (g.phase === "playing" && !g.paused) {
      g.tick(clampedDt);
      const speed = useGame.getState().speed;
      const diff = activeDifficulty();
      worldScroll.current += speed * clampedDt;

      const stageNow = activeStage();
      const seed = g.runSeed;
      const lookahead = g.distance + TRACK_LOOKAHEAD;
      while (seed && nextWaveAt.current <= lookahead) {
        const wave = makeWave(
          seed,
          nextWaveIndex.current,
          diff,
          stageNow,
          nextWaveAt.current,
        );
        const spawnZ = PLAYER_Z - (wave.d - g.distance) - wave.hazardZJitter;
        for (const h of wave.hazards) {
          hazards.current.push({
            id: nextId++,
            z: spawnZ,
            prevZ: spawnZ,
            lane: h.lane,
            kind: h.kind,
            halfX: HAZARD_BOX[h.kind].halfX,
            halfZ: HAZARD_BOX[h.kind].halfZ,
          });
        }
        if (wave.coinLane != null) {
          pickups.current.push({
            id: nextId++,
            z: spawnZ + 8,
            lane: wave.coinLane,
            kind: "coin",
            x: LANES[wave.coinLane],
          });
        }
        if (wave.jumpLane != null) {
          pickups.current.push({
            id: nextId++,
            z: spawnZ + 4 - wave.jumpZJitter,
            lane: wave.jumpLane,
            kind: "jump",
            x: LANES[wave.jumpLane],
          });
        }
        nextWaveIndex.current += 1;
        nextWaveAt.current += waveGap(diff, nextWaveAt.current);
      }

      for (const h of hazards.current) {
        h.prevZ = h.z;
        const drive =
          h.kind === "rival" || h.kind === "truck"
            ? Math.min(diff.rivalDriveSpeed, speed * 0.55)
            : 0;
        h.z += Math.max(2.5, speed - drive) * clampedDt;
      }
      // Traffic: cars stop behind crates/barriers/other cars instead of ghosting.
      for (const lane of [0, 1, 2]) {
        stackLane(hazards.current.filter((h) => h.lane === lane));
      }
      for (const p of pickups.current) p.z += speed * clampedDt;

      // Lerp visual X first so collision matches what's on screen this frame.
      if (player.current) {
        const targetX = LANES[g.lane];
        player.current.position.x = THREE.MathUtils.lerp(
          player.current.position.x,
          targetX,
          1 - Math.exp(-LANE_LERP * clampedDt),
        );
      }
      const playerX = player.current?.position.x ?? LANES[g.lane];

      // Record ghost samples of this run (committed on PB in finishToResult).
      ghostSampleAcc.current += clampedDt;
      if (ghostSampleAcc.current >= 1 / 12 && player.current) {
        ghostSampleAcc.current = 0;
        useGame.getState().recordGhostSample({
          d: useGame.getState().distance,
          x: player.current.position.x,
          y: player.current.position.y,
        });
      }

      // Magnet: pull coins toward the player.
      if (magnetOn) {
        for (const p of pickups.current) {
          if (p.kind !== "coin") continue;
          const px = p.x ?? LANES[p.lane];
          const dz = p.z - PLAYER_Z;
          const dx = px - playerX;
          const dist = Math.hypot(dx, dz);
          if (dist < MAGNET_RADIUS && dist > 0.01) {
            const pull = Math.min(1, (18 * clampedDt) / dist);
            p.x = px + (playerX - px) * pull;
            p.z = p.z + (PLAYER_Z - p.z) * pull * 0.85;
          }
        }
      }

      const invuln = now < g.invulnerableUntil;
      const airborne = g.jumpTimer > 0;
      const crushed: number[] = [];
      const pHalfX = playerHalf.current.x;
      const pHalfZ = playerHalf.current.z;
      for (const h of hazards.current) {
        if (!playerHitsHazard(playerX, h, pHalfX, pHalfZ)) continue;
        if (phaseOn) continue;
        // Jump crates, barriers, holes, cars — not trucks (too tall).
        if (airborne && jumpClears(h.kind)) continue;
        if (crushCrates && h.kind === "box") {
          crushed.push(h.id);
          useGame.getState().setShake(0.35);
          sfx.crush();
          continue;
        }
        if (!invuln) {
          useGame.getState().crash();
          break;
        }
      }
      if (crushed.length) {
        hazards.current = hazards.current.filter((h) => !crushed.includes(h.id));
      }

      pickups.current = pickups.current.filter((p) => {
        const px = p.x ?? LANES[p.lane];
        if (hitsPickup(playerX, px, p.z, magnetOn && p.kind === "coin")) {
          if (p.kind === "jump") {
            useGame.getState().addJumpToken();
            sfx.jumpPickup();
          } else {
            useGame.getState().addRunCoin();
            sfx.coin();
          }
          return false;
        }
        return p.z < 14;
      });
      hazards.current = hazards.current.filter((h) => h.z < 16);
    }

    if (dashGroup.current) {
      dashGroup.current.position.z = worldScroll.current % 3.2;
    }
    if (cityGroup.current) {
      const step =
        useGame.getState().phase === "playing" && !useGame.getState().paused
          ? useGame.getState().speed * clampedDt * 0.9
          : 0;
      for (const child of cityGroup.current.children) {
        child.position.z += step;
        if (child.position.z > 18) child.position.z -= 130;
      }
    }

    if (rainRef.current) {
      // Throttle rain: every 2nd frame while playing, every 4th in menus.
      rainFrame.current += 1;
      const every = g.phase === "playing" && !g.paused ? 2 : 4;
      if (rainFrame.current % every === 0) {
        const arr = rainRef.current.geometry.attributes.position
          .array as Float32Array;
        const spd = g.phase === "playing" && !g.paused ? useGame.getState().speed : 8;
        const step = clampedDt * every;
        for (let i = 0; i < arr.length; i += 3) {
          arr[i + 1] -= (8 + spd * 0.15) * step;
          arr[i + 2] += spd * 0.35 * step;
          if (arr[i + 1] < 0) {
            arr[i + 1] = 10 + Math.random() * 4;
            arr[i + 2] = -Math.random() * 70;
          }
          if (arr[i + 2] > 12) arr[i + 2] = -70;
        }
        rainRef.current.geometry.attributes.position.needsUpdate = true;
      }
    }

    const shown = equippedVehicle();
    const stageNow = activeStage();
    // Menu shows the vehicle in the dedicated GaragePreview canvas only —
    // keep the track empty so there's no duplicate/oversized car underneath.
    if (player.current && g.phase === "menu") {
      player.current.visible = false;
      if (ghostRef.current) ghostRef.current.visible = false;
    } else if (player.current) {
      const targetX = LANES[g.lane];
      if (g.phase !== "playing") {
        player.current.position.x = THREE.MathUtils.lerp(
          player.current.position.x,
          targetX,
          1 - Math.exp(-LANE_LERP * clampedDt),
        );
      }
      const jumpT = useGame.getState().jumpTimer;
      const jumpDur = Math.max(0.01, useGame.getState().jumpDuration);
      const jumpLift =
        jumpT > 0
          ? Math.sin((1 - jumpT / jumpDur) * Math.PI) *
            GAME.jumpHeight *
            shown.jumpMult *
            stageNow.jumpMult
          : 0;
      player.current.position.y =
        0.42 +
        jumpLift +
        (g.paused
          ? 0
          : Math.sin(state.clock.elapsedTime * 10) * (jumpT > 0 ? 0.01 : 0.03));
      player.current.position.z = PLAYER_Z;
      const lean = (targetX - player.current.position.x) * 0.42;
      player.current.rotation.z = lean;
      // No yaw — it swung the visual car into the next lane off the hitbox.
      player.current.rotation.y = 0;
      player.current.rotation.x = jumpLift > 0.2 ? -0.18 : 0;
      const invuln = now < g.invulnerableUntil;
      player.current.visible =
        phaseOn ||
        !invuln ||
        Math.floor(state.clock.elapsedTime * 14) % 2 === 0;

      // Ghost of personal best — visual only, no collision.
      if (ghostRef.current) {
        const samples = g.ghostByDifficulty[g.difficulty] ?? [];
        const pose =
          g.phase === "playing" && g.ghostOn && samples.length
            ? (() => {
                const dist = useGame.getState().distance;
                if (dist <= samples[0]!.d) {
                  return { x: samples[0]!.x, y: samples[0]!.y };
                }
                const last = samples[samples.length - 1]!;
                if (dist >= last.d) return null;
                let lo = 0;
                let hi = samples.length - 1;
                while (lo < hi - 1) {
                  const mid = (lo + hi) >> 1;
                  if (samples[mid]!.d <= dist) lo = mid;
                  else hi = mid;
                }
                const a = samples[lo]!;
                const b = samples[hi]!;
                const t = (dist - a.d) / Math.max(0.001, b.d - a.d);
                return {
                  x: a.x + (b.x - a.x) * t,
                  y: a.y + (b.y - a.y) * t,
                };
              })()
            : null;
        if (pose) {
          ghostRef.current.visible = true;
          ghostRef.current.position.set(pose.x, pose.y, PLAYER_Z);
        } else {
          ghostRef.current.visible = false;
        }
      } else if (ghostRef.current) {
        ghostRef.current.visible = false;
      }

      // Phase power: translucent vehicle while active (cached materials).
      const matsKey = shown.id;
      if (playerMatsKey.current !== matsKey) {
        playerMats.current = [];
        player.current.traverse((child) => {
          const mesh = child as THREE.Mesh;
          if (!mesh.isMesh) return;
          const mats = Array.isArray(mesh.material)
            ? mesh.material
            : [mesh.material];
          for (const m of mats) {
            const mat = m as THREE.MeshStandardMaterial;
            if (!mat || !("opacity" in mat)) continue;
            if (mat.userData._baseOpacity == null) {
              mat.userData._baseOpacity = mat.opacity ?? 1;
            }
            playerMats.current.push(mat);
          }
        });
        playerMatsKey.current = matsKey;
      }
      for (const mat of playerMats.current) {
        const target = phaseOn ? 0.4 : mat.userData._baseOpacity;
        mat.transparent = phaseOn || mat.userData._baseOpacity < 1;
        mat.opacity = THREE.MathUtils.lerp(mat.opacity, target, 0.2);
        mat.depthWrite = !phaseOn;
      }
    }

    if (trailPts.current && player.current) {
      const pos = trailPts.current.geometry.attributes.position
        .array as Float32Array;
      const px = player.current.position.x;
      for (let i = trailBuf.n - 1; i > 0; i--) {
        pos[i * 3] = pos[(i - 1) * 3] * 0.92 + px * 0.08;
        pos[i * 3 + 1] = 0.25;
        pos[i * 3 + 2] = PLAYER_Z + 0.4 + i * 0.11;
      }
      pos[0] = px;
      pos[1] = 0.25;
      pos[2] = PLAYER_Z + 0.55;
      trailPts.current.geometry.attributes.position.needsUpdate = true;
      const mat = trailPts.current.material as THREE.PointsMaterial;
      mat.color.set(shown.trail);
      mat.opacity = g.phase === "playing" ? 0.85 : 0.35;
    }

    const diffCam = DIFFICULTIES[g.difficulty] ?? DIFFICULTIES.medium;
    const speed =
      g.phase === "playing" ? useGame.getState().speed : diffCam.baseSpeed;
    const span = Math.max(0.05, diffCam.maxSpeed - diffCam.baseSpeed);
    const baseFov =
      50 +
      ((speed / Math.max(v.speedMult, 0.01) - diffCam.baseSpeed) / span) * 8;
    if ("fov" in camera) {
      (camera as THREE.PerspectiveCamera).fov = THREE.MathUtils.lerp(
        (camera as THREE.PerspectiveCamera).fov,
        baseFov,
        0.08,
      );
      (camera as THREE.PerspectiveCamera).updateProjectionMatrix();
    }
    const shake = useGame.getState().shake;
    camera.position.x = Math.sin(state.clock.elapsedTime * 40) * shake * 0.18;
    camera.position.y =
      5.8 + Math.cos(state.clock.elapsedTime * 35) * shake * 0.12;
    camera.position.z = 11.2;

    showHitbox.current = hitboxesEnabled();
    if (player.current && g.phase !== "menu" && state.clock.getElapsedTime() > 0) {
      const m = measurePlayer(player.current);
      if (m.halfX > 0.15 && m.halfZ > 0.15) {
        playerHalf.current.x = m.halfX * VISUAL_FIT;
        playerHalf.current.z = m.halfZ * VISUAL_FIT;
      }
    }
    if (playerHitbox.current) {
      playerHitbox.current.visible = showHitbox.current;
      const hx = playerHalf.current.x;
      const hz = playerHalf.current.z;
      playerHitbox.current.scale.set(
        hx / PLAYER_HALF_X,
        1,
        hz / PLAYER_HALF_Z,
      );
    }

    syncEntities(
      hazardGroup.current,
      hazards.current,
      pickups.current,
      showHitbox.current,
    );
  });

  return (
    <>
      <color attach="background" args={[stage.fog]} />
      <fog attach="fog" args={[stage.fog, 28, 90]} />
      <ambientLight
        intensity={
          stage.id === "lunar" ? 0.62 : stage.id === "ice" ? 0.7 : 0.48
        }
      />
      <directionalLight
        position={[3, 12, 4]}
        intensity={
          stage.id === "volcano" ? 1.25 : stage.id === "ice" ? 1.15 : 1.05
        }
        color={
          stage.id === "volcano"
            ? "#FED7AA"
            : stage.id === "ice"
              ? "#E0F2FE"
              : "#CFFAFE"
        }
      />
      <pointLight
        position={[0, 3.5, 3]}
        intensity={1.8}
        color={stage.buildingHueA}
        distance={28}
      />
      <pointLight
        position={[-4, 2, -8]}
        intensity={1.1}
        color={stage.buildingHueB}
        distance={30}
      />

      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, -20]} receiveShadow>
        <planeGeometry args={[7.2, 100]} />
        <meshStandardMaterial
          color={stage.ground}
          roughness={0.92}
          metalness={0.15}
        />
      </mesh>
      {[-3.55, 3.55].map((x) => (
        <mesh key={x} position={[x, 0.02, -20]} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[0.35, 100]} />
          <meshBasicMaterial color={stage.rail} transparent opacity={0.35} />
        </mesh>
      ))}

      <group ref={dashGroup}>
        {dashes.map((d, i) => (
          <mesh
            key={i}
            rotation={[-Math.PI / 2, 0, 0]}
            position={[d.x, 0.025, d.z]}
          >
            <planeGeometry args={[0.1, 1.5]} />
            <meshBasicMaterial color="#475569" />
          </mesh>
        ))}
      </group>

      {[-3.35, 3.35].map((x) => (
        <mesh key={`rail-${x}`} position={[x, 0.35, -20]}>
          <boxGeometry args={[0.12, 0.7, 100]} />
          <meshStandardMaterial
            color={stage.rail}
            emissive={stage.railEmissive}
            emissiveIntensity={1.6}
            toneMapped={false}
          />
        </mesh>
      ))}

      <group ref={cityGroup}>
        {buildings.map((b, i) => (
          <group key={i} position={[b.x, b.h / 2, b.z]}>
            <mesh>
              <boxGeometry args={[b.w, b.h, 2.2]} />
              <meshStandardMaterial color="#111827" roughness={0.85} />
            </mesh>
            <mesh position={[0, b.h * 0.15, 1.12]}>
              <boxGeometry args={[b.w * 0.7, b.h * 0.35, 0.05]} />
              <meshStandardMaterial
                color={b.hue}
                emissive={b.hue}
                emissiveIntensity={1.2}
                toneMapped={false}
              />
            </mesh>
          </group>
        ))}
      </group>

      <mesh position={[0, 2.2, -55]}>
        <planeGeometry args={[40, 8]} />
        <meshBasicMaterial
          color={
            stage.id === "ice"
              ? "#0B2744"
              : stage.id === "volcano"
                ? "#3B0A08"
                : stage.id === "lunar"
                  ? "#1E293B"
                  : "#1e1b4b"
          }
          transparent
          opacity={0.55}
        />
      </mesh>

      {stage.id === "neon" || stage.id === "volcano" ? (
        <points ref={rainRef} geometry={rainGeo}>
          <pointsMaterial
            color="#94A3B8"
            size={0.05}
            transparent
            opacity={0.45}
            depthWrite={false}
          />
        </points>
      ) : null}

      <points ref={trailPts}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            args={[trailBuf.pos, 3]}
          />
        </bufferGeometry>
        <pointsMaterial
          color="#67E8F9"
          size={0.14}
          transparent
          opacity={0.8}
          depthWrite={false}
          sizeAttenuation
        />
      </points>

      <group ref={player}>
        <VehicleModel key={vehicle.id} vehicle={vehicle} axis="z" />
        <mesh ref={playerHitbox} visible={false} position={[0, 0.2, 0]}>
          <boxGeometry args={[PLAYER_HALF_X * 2, 0.55, PLAYER_HALF_Z * 2]} />
          <meshBasicMaterial
            color="#22d3ee"
            wireframe
            transparent
            opacity={0.95}
            depthTest={false}
          />
        </mesh>
      </group>

      <group ref={ghostRef} visible={false}>
        <VehicleModel
          key={`ghost-${vehicle.id}`}
          vehicle={vehicle}
          axis="z"
          ghost
        />
      </group>

      <group ref={hazardGroup} />
    </>
  );
}

const hazardMeshes = new Map<number, THREE.Object3D>();
const pickupMeshes = new Map<number, THREE.Object3D>();

function syncEntities(
  group: THREE.Group | null,
  hazards: Hazard[],
  pickups: Pickup[],
  showHitbox: boolean,
) {
  if (!group) return;
  const liveH = new Set(hazards.map((h) => h.id));
  const liveP = new Set(pickups.map((p) => p.id));

  for (const [id, obj] of hazardMeshes) {
    if (!liveH.has(id)) {
      group.remove(obj);
      disposeHazardObject(obj);
      hazardMeshes.delete(id);
    }
  }
  for (const [id, obj] of pickupMeshes) {
    if (!liveP.has(id)) {
      group.remove(obj);
      disposeObj(obj);
      pickupMeshes.delete(id);
    }
  }

  for (const h of hazards) {
    let obj = hazardMeshes.get(h.id);
    if (!obj) {
      obj = makeHazardObject(h.kind);
      const fitted = centerOnAabb(obj);
      if (h.kind === "rival" || h.kind === "truck") plantOnGround(obj);
      h.halfX = fitted.halfX * VISUAL_FIT;
      h.halfZ = fitted.halfZ * VISUAL_FIT;
      attachHitbox(
        obj,
        h.halfX,
        h.kind === "truck" ? 1.15 : 0.45,
        h.halfZ,
        h.kind === "hole" ? "#f97316" : h.kind === "truck" ? "#f59e0b" : "#ef4444",
      );
      hazardMeshes.set(h.id, obj);
      group.add(obj);
    }
    const y =
      h.kind === "rival" || h.kind === "truck"
        ? 0
        : h.kind === "hole"
          ? 0.02
          : 0.42;
    obj.position.set(LANES[h.lane], y, h.z);
    setHitboxVisible(obj, showHitbox);
  }
  for (const p of pickups) {
    let obj = pickupMeshes.get(p.id);
    if (!obj) {
      obj = p.kind === "jump" ? makeJumpToken() : makeCoin();
      attachHitbox(
        obj,
        PICKUP_HALF_X,
        0.35,
        PICKUP_HALF_Z,
        p.kind === "jump" ? "#a78bfa" : "#fbbf24",
      );
      pickupMeshes.set(p.id, obj);
      group.add(obj);
    }
    const bob =
      p.kind === "jump"
        ? 0.95 + Math.sin(p.z * 0.55) * 0.1
        : 0.75 + Math.sin(p.z * 0.5) * 0.08;
    const x = p.x ?? LANES[p.lane];
    obj.position.set(x, bob, p.z);
    if (p.kind === "jump") {
      obj.rotation.y += 0.06;
    } else {
      obj.rotation.y += 0.1;
    }
    setHitboxVisible(obj, showHitbox);
  }
}

function measurePlayer(root: THREE.Object3D): { halfX: number; halfZ: number } {
  const box = new THREE.Box3();
  const tmp = new THREE.Box3();
  let any = false;
  root.updateMatrixWorld(true);
  root.traverse((child) => {
    if (child.name === "__hitbox") return;
    const mesh = child as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry || !mesh.visible) return;
    mesh.geometry.computeBoundingBox();
    if (!mesh.geometry.boundingBox) return;
    tmp.copy(mesh.geometry.boundingBox).applyMatrix4(mesh.matrixWorld);
    const size = new THREE.Vector3();
    tmp.getSize(size);
    if (size.y < 0.02 && size.x * size.z > 0.1) return;
    if (!any) {
      box.copy(tmp);
      any = true;
    } else box.union(tmp);
  });
  if (!any) return { halfX: PLAYER_HALF_X, halfZ: PLAYER_HALF_Z };
  const size = new THREE.Vector3();
  box.getSize(size);
  return {
    halfX: Math.max(0.2, size.x / 2),
    halfZ: Math.max(0.25, size.z / 2),
  };
}

function attachHitbox(
  parent: THREE.Object3D,
  halfX: number,
  halfY: number,
  halfZ: number,
  color: string,
) {
  const mesh = new THREE.Mesh(
    new THREE.BoxGeometry(halfX * 2, halfY * 2, halfZ * 2),
    new THREE.MeshBasicMaterial({
      color,
      wireframe: true,
      transparent: true,
      opacity: 0.95,
      depthTest: false,
    }),
  );
  mesh.name = "__hitbox";
  mesh.position.y = halfY;
  mesh.visible = false;
  parent.add(mesh);
}

function setHitboxVisible(obj: THREE.Object3D, on: boolean) {
  const hb = obj.getObjectByName("__hitbox");
  if (hb) hb.visible = on;
}

function disposeObj(obj: THREE.Object3D) {
  obj.traverse((c) => {
    const m = c as THREE.Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = m.material;
    if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
    else if (mat) mat.dispose();
  });
}

function makeCoin() {
  return makeCoinMesh();
}

function makeJumpToken() {
  return makeJumpTokenMesh();
}
